import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import cn from 'classnames';

import RangeSelect from '../Notifications/elements/RangeSelect';
import Stat, { rate } from './ShopMyDMStat';
import { getShopMyDmSummary, getShopMyDmStatus } from '../../api/shopmyDm';
import { REASONS, TIPS } from './shopmyDmCopy';

const fmt = value => Number(value || 0).toLocaleString('en-US');

// The windows roll back from now, the same hours the Notifications tab uses.
const rangeCovering = createdAt => {
  const hours = moment().diff(createdAt, 'hours');
  if (hours < 7 * 24) return '7d';
  if (hours < 30 * 24) return '30d';
  return '90d';
};

const ShopMyDMAnalytics = ({ onOpenCreator }) => {
  const [range, setRange] = useState('7d');
  const [summary, setSummary] = useState(null);
  const [status, setStatus] = useState(null);
  const [showStuck, setShowStuck] = useState(false);

  useEffect(() => {
    let current = true;
    getShopMyDmSummary(range)
      .then(data => current && setSummary(data))
      .catch(() => current && window.ALERT?.error?.('Failed to load ShopMy DM analytics.'));
    return () => {
      current = false;
    };
  }, [range]);

  useEffect(() => {
    let current = true;
    getShopMyDmStatus()
      .then(data => current && setStatus(data))
      .catch(() => current && window.ALERT?.error?.('Failed to load ShopMy DM status.'));
    return () => {
      current = false;
    };
  }, []);

  const totals = summary?.totals;
  const peak = key => Math.max(1, ...(summary?.trend || []).map(day => day[key]));

  return (
    <div className='shopmy-dm-analytics' data-testid='shopmy-dm-analytics'>
      <p className='lede'>What ShopMy DM did across flagged creators. Each comment gets one DM, either a link or a collection, so the two are counted separately.</p>
      <div className='filters'>
        <RangeSelect value={range} onChange={setRange} options={['24h', '7d', '30d', '90d']} />
        <span className='note'>Every flagged creator</span>
      </div>
      {status && (
        <div className='strip' data-testid='shopmy-dm-status'>
          <div>
            <span className='dot' />
            {fmt(status.arrivedLastHour)} comments arrived in the last hour
          </div>
          {status.shadowOn && (
            <div className='warn'>
              <span className='dot' />
              Shadow is on. Qualified comments are not being sent.
            </div>
          )}
          <div className={cn({ warn: status.hourlyCap === 0 })}>
            <span className='dot' />
            {status.hourlyCap === 0 ? 'Hourly cap is 0. Every send is being skipped.' : `Hourly cap is ${fmt(status.hourlyCap)} per creator`}
          </div>
          <div className={cn({ warn: status.stuckCount > 0 })}>
            <span className='dot' />
            {fmt(status.stuckCount)} comments are still marked received
            {status.stuckCount > 0 && (
              <button type='button' className='link' onClick={() => setShowStuck(open => !open)}>{showStuck ? 'Hide' : 'View'}</button>
            )}
          </div>
        </div>
      )}
      {showStuck && (
        <div className='card stuck' data-testid='shopmy-dm-stuck'>
          <div className='card-head'>
            <h2>Still received</h2>
            <span className='note'>Older than {status?.stuckAfterMinutes} minutes. The worker will not pick these up again. Open one to see the row.</span>
          </div>
          <div className='grid head'>
            <span className='label'>Creator</span><span className='label'>Arrived</span><span className='label'>Comment</span>
          </div>
          {(status?.stuck || []).map(row => (
            <button key={row.id} type='button' className='grid open' onClick={() => onOpenCreator(row.User_id, 'received', rangeCovering(row.createdAt))}>
              <span><span className='name'>{row.name}</span> <span className='handle'>@{row.username}</span></span>
              <span>{moment(row.createdAt).fromNow()}</span>
              <span><span className='commenter'>@{row.commenterUsername}</span> {row.commentText}</span>
            </button>
          ))}
        </div>
      )}
      {totals && (
        <div className='card'>
          <div className='stats'>
            <Stat value={fmt(totals.sent)} label='Sent' />
            {status?.shadowOn && <Stat value={fmt(totals.shadow)} label='Shadow' tip={TIPS.shadow} />}
            <Stat value={fmt(totals.skipped)} label='Skipped' tip={TIPS.skipped} />
            <Stat value={fmt(totals.failed)} label='Failed' tip={TIPS.failed} />
            <Stat value={fmt(totals.clicked)} label='Clicked' />
            <Stat value={rate(totals.clicked, totals.sent)} label='Click rate' />
          </div>
        </div>
      )}
      {summary && (
        <div className='split'>
          <Trend title='Sent' tone='sent' values={summary.trend} field='sent' peak={peak('sent')} />
          <Trend title='Failed' tone='failed' values={summary.trend} field='failed' peak={peak('failed')} />
        </div>
      )}
      {summary && (
        <div className='split'>
          <KindCard title='Links' row={summary.kinds.link} shadowOn={status?.shadowOn} />
          <KindCard title='Collections' row={summary.kinds.collection} shadowOn={status?.shadowOn} />
        </div>
      )}
      <div className='card reasons'>
        <div className='card-head'>
          <h2>Why a comment didn’t send</h2>
        </div>
        <div className='grid head'>
          <span className='label'>Reason</span><span className='label'>Kind</span>
          <span className='label right'>Total</span><span className='label right'>Links</span><span className='label right'>Collections</span>
        </div>
        {(summary?.reasons || []).map(reason => (
          <React.Fragment key={reason.key}>
            <div className='grid'>
              <span>{REASONS[reason.key] || reason.key}</span>
              <span><span className={cn('pill', reason.status)}>{reason.status}</span></span>
              <span className='num'>{fmt(reason.total)}</span>
              <span className='num'>{fmt(reason.links)}</span>
              <span className='num'>{fmt(reason.collections)}</span>
            </div>
            {reason.errors.map(error => (
              <div key={error.message} className='grid sub'>
                <span>“{error.message}”{error.code != null ? ` · code ${error.code}` : ''}</span>
                <span />
                <span className='num'>{fmt(error.total)}</span>
                <span className='num'>{fmt(error.links)}</span>
                <span className='num'>{fmt(error.collections)}</span>
              </div>
            ))}
          </React.Fragment>
        ))}
        {summary && !summary.reasons.length && <div className='empty'>Every comment in this range sent.</div>}
      </div>
    </div>
  );
};

const Trend = ({ title, tone, values, field, peak }) => (
  <div className={cn('card panel trend', tone)}>
    <div className='panel-head'>
      <h2>{title}</h2>
      <span className='note'>Scaled to its own peak · {fmt(peak)}</span>
    </div>
    <div className='bars'>
      {values.map(day => (
        <div key={day.day} className='bar' title={`${day.day} · ${day[field]}`}>
          <div className='plot'>
            <div className='fill' style={{ height: `${Math.round((day[field] / peak) * 100)}%` }} />
          </div>
          <span className='tick'>{moment(day.day).format(values.length > 10 ? 'D' : 'ddd')}</span>
        </div>
      ))}
    </div>
  </div>
);

Trend.propTypes = {
  title: PropTypes.string.isRequired,
  tone: PropTypes.string.isRequired,
  values: PropTypes.array.isRequired,
  field: PropTypes.string.isRequired,
  peak: PropTypes.number.isRequired
};

const KindCard = ({ title, row, shadowOn }) => (
  <div className='card kv'>
    <div className='panel-head'>
      <h2>{title}</h2>
    </div>
    {[
      ['Sent', fmt(row.sent)],
      ...(shadowOn ? [['Shadow', fmt(row.shadow)]] : []),
      ['Skipped', fmt(row.skipped)],
      ['Failed', fmt(row.failed)],
      ['Received', fmt(row.received)],
      ['Clicked', fmt(row.clicked)],
      ['Click rate', rate(row.clicked, row.sent)]
    ].map(([label, value]) => (
      <div key={label} className='row'>
        <span>{label}</span>
        <span>{value}</span>
      </div>
    ))}
  </div>
);

KindCard.propTypes = {
  title: PropTypes.string.isRequired,
  row: PropTypes.object.isRequired,
  shadowOn: PropTypes.bool
};

ShopMyDMAnalytics.propTypes = {
  onOpenCreator: PropTypes.func.isRequired
};

export default ShopMyDMAnalytics;
